/**
 * Mídia real (Meta Ads) no modo ao vivo: investimento, custo com imposto, CPA, ROAS e os outros KPIs
 * de mídia, as séries de gasto, a tabela do Tráfego, os Criativos e o ROAS do funil e o CPA da Meta
 * (snapshot e V2). Venda de anúncio conta pela Meta (Gerenciador); a Kiwify pelo link é só referência.
 *
 * Sem Meta (fora do ar, não conectada ou ainda sem os números de hoje) nada vem da simulação: os KPIs
 * de mídia ficam sem valor com o motivo, e tabela, gráficos e criativos mostram o erro da fonte.
 */
import type {
  Ad,
  AdAccount,
  AdSet,
  Campaign,
  ColumnarSeries,
  Filters,
  Freshness,
  InsightQuery,
  InsightRow,
  InsightTable,
  IntegrationStatus,
  KpiDef,
  KpiId,
  KpiValue,
  ListQuery,
  MetaEffectiveStatus,
  Page,
  Query,
  SeriesQuery,
} from '@rota/contracts';
import { type AdRulers, buildAdRulers, withAdRulers } from '@/lib/adRulers';
import { offMetaDims } from '../metaCut';
import { getWithRetry } from './http';
import { LIVE_KPI_TEXTS } from './liveKpiTexts';
import {
  BRT,
  bucketOf,
  buckets,
  CACHE_MS,
  compareMs,
  cumulative,
  DAY,
  dayStart,
  finalOf,
  HOUR,
  isPaid,
  kiwifyByLink,
  kv,
  matches,
  noRealKpi,
  orderDates,
  type RealCtx,
  type RealKpiInput,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  revenueOf,
  row0,
  setKpiV2,
  sparkSize,
  type Totals,
  todayKey,
  totals,
} from './realCore';
import { fixedCostsIn } from './realFinance';
import {
  accountName,
  cutLevel,
  daysOf,
  effectiveStatus,
  type FetchInsights,
  fetchRealInsights,
  filterRows,
  hasEntityFilter,
  insightTable,
  type MetaSyncFields,
  metaUnavailable,
  type RealInsightDay,
  type RealInsightRow,
  type RealInsights,
  rollUp,
} from './realTraffic';

/** Mídia real do período (Meta Ads), como o servidor manda (`GET /api/painel/midia`). */
export interface RealMedia extends MetaSyncFields {
  configured: boolean;
  totals: {
    spendCents: number;
    /** Custo real (impostos da Meta na conta em real; câmbio do cartão + IOF na de dólar); ausente em servidor antigo. */
    costCents?: number;
    impressions: number;
    reach: number;
    clicks: number;
    linkClicks: number;
    lpv: number;
  };
  sales: { paid: number; front: number; grossCents: number; commissionCents: number; unmatched: number };
  /**
   * Compras que a Meta atribui aos anúncios, com o preço cheio; ausente em servidor antigo. `upsell` =
   * as do 20X, separadas pelo valor (servidor novo): o CPA conta só as do curso.
   */
  meta?: { purchases: number; valueCents: number; upsell?: number };
  /**
   * As mesmas compras só pela janela de 7 dias do clique (a régua "Meta só clique"). null = algum dia
   * do período foi lido antes de o servidor guardar essa janela; ausente em servidor antigo.
   */
  metaClick?: { purchases: number; valueCents: number; upsell?: number } | null;
  /** `costCents` (com impostos/câmbio/IOF) vem de servidor novo; sem ele, o gasto. */
  hourly: { t: number; spendCents: number; costCents?: number }[];
  /**
   * O mesmo gasto por hora separado por conta (id → horas): o filtro de conta mostra a hora da própria
   * conta. Ausente em servidor antigo (aí o filtro de conta fica sem a hora).
   */
  hourlyByAccount?: Record<string, { t: number; spendCents: number; costCents?: number }[]>;
  /** Por dia; atividade e compras da Meta vêm de servidor novo (ausentes no antigo). */
  daily: {
    date: string;
    spendCents: number;
    costCents?: number;
    impressions?: number;
    linkClicks?: number;
    lpv?: number;
    metaPurchases?: number;
    metaUpsell?: number;
    metaValueCents?: number;
  }[];
  /**
   * Período que termina no meio de um dia já passado ("ontem até a mesma hora"): o gasto, o custo, as
   * impressões e os cliques desse dia vieram do detalhe por hora até `until` (`byHour`), ou ficaram do
   * dia inteiro porque falta o detalhe por hora (`byHour` false: não serve para comparar). Compras,
   * valor e visitas não têm hora: nesse dia são do dia inteiro. Ausente em servidor antigo.
   */
  cut?: { day: string; until: number; byHour: boolean };
}
/**
 * `null` = Meta não conectada no servidor. Falha (rede, 500) lança `source_unavailable`: não é a
 * mesma coisa que "não conectada" e nunca vira a simulação.
 */
export type FetchMedia = (from: number, to: number) => Promise<RealMedia | null>;

export const fetchRealMedia: FetchMedia = async (from, to) => {
  const r = await getWithRetry(`/api/painel/midia?from=${from}&to=${to}`);
  if (!r.ok) throw metaUnavailable(`Meta com erro (HTTP ${r.status})`);
  const j = (await r.json()) as RealMedia;
  return j.configured ? j : null;
};

/** Por que não há mídia real num intervalo: a última leitura falhou (com a mensagem) ou a Meta não está conectada. */
export type MediaGap = { kind: 'error'; message: string } | { kind: 'not_configured' };

/** Falhas recentes de cada fonte de mídia (a fonte é achada pelo `loadMedia` que o contexto carrega). */
const gaps = new WeakMap<object, Map<string, MediaGap>>();

/**
 * Mídia e tabela da Meta com cache curto por intervalo (o stream limpa quando a Meta sincroniza). Uma
 * falha não fica no cache (a próxima tela tenta de novo) e o `loadMedia`/`loadInsights` resolvem
 * `null` (os outros módulos seguem com as vendas); `mediaGap` diz se o `null` foi erro ou falta de conexão.
 */
export function createMediaSource(fetchMedia: FetchMedia, fetchInsights: FetchInsights, clock: () => number) {
  const missing = new Map<string, MediaGap>();
  const insightsCache = new Map<string, { at: number; p: ReturnType<FetchInsights> }>();
  const loadInsights: FetchInsights = (from, to, level) => {
    const key = `${from}|${to}|${level}`;
    const hit = insightsCache.get(key);
    if (hit && clock() - hit.at < CACHE_MS) return hit.p;
    const p = fetchInsights(from, to, level).then(
      (x) => {
        missing.delete(`i|${key}`);
        return x;
      },
      (e: unknown) => {
        insightsCache.delete(key);
        missing.set(`i|${key}`, { kind: 'error', message: errorText(e) });
        return null;
      },
    );
    insightsCache.set(key, { at: clock(), p });
    return p;
  };
  const mediaCache = new Map<string, { at: number; p: Promise<RealMedia | null> }>();
  const loadMedia = (from: number, to: number): Promise<RealMedia | null> => {
    const key = `${from}|${to}`;
    const hit = mediaCache.get(key);
    if (hit && clock() - hit.at < CACHE_MS) return hit.p;
    const p = fetchMedia(from, to).then(
      (x) => {
        if (x) missing.delete(`m|${key}`);
        else missing.set(`m|${key}`, { kind: 'not_configured' });
        return x;
      },
      (e: unknown) => {
        mediaCache.delete(key);
        missing.set(`m|${key}`, { kind: 'error', message: errorText(e) });
        return null;
      },
    );
    mediaCache.set(key, { at: clock(), p });
    return p;
  };
  const invalidate = () => {
    insightsCache.clear();
    mediaCache.clear();
  };
  gaps.set(loadMedia, missing);
  gaps.set(loadInsights, missing);
  return { loadMedia, loadInsights, invalidate };
}
export { fetchRealInsights };

const errorText = (e: unknown): string =>
  e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' && e.message
    ? e.message
    : 'Meta com erro';

/** Motivo do `null` do `loadMedia` (ou do `loadInsights`, com `level`) naquele intervalo. */
export function mediaGap(
  load: RealCtx['loadMedia'] | RealCtx['loadInsights'],
  from: number,
  to: number,
  level?: 'campaign' | 'adset' | 'ad',
): MediaGap | null {
  const m = gaps.get(load);
  return m?.get(level ? `i|${from}|${to}|${level}` : `m|${from}|${to}`) ?? null;
}

/** Igual ao SLA da Meta das telas (`SOURCE_SLA_MS.meta`): sincronização de hoje mais velha que isto = atrasada. */
export const META_SLA_MS = 35 * 60_000;

/** Textos de "sem dado real" da Meta (cartões, tabela e gráficos). */
export const META_GAP = {
  error: 'Meta com erro: sem os números dos anúncios agora',
  notConfigured: 'Meta não conectada',
  noToday: 'Meta ainda sem dados de hoje',
} as const;

const gapText = (g: MediaGap | null): string =>
  g?.kind === 'not_configured' ? META_GAP.notConfigured : META_GAP.error;

/**
 * Frescor real da Meta num período. A hora é a da última leitura bem-sucedida da parte mostrada (hoje,
 * se o período inclui hoje; senão, a linha mais nova do período). Fica atrasado (amarelo) quando a
 * última tentativa falhou, quando hoje está sem leitura há mais que o SLA, ou quando um dia fechado do
 * período foi lido antes de terminar. Dia fechado lido depois do fim não envelhece: é número final.
 */
export function metaFreshness(now: number, x: MetaSyncFields, r: { from: number; to: number }): Freshness[] {
  const todayStart = dayStart(todayKey(now));
  const withToday = r.to > todayStart;
  const asOf = withToday ? (x.todayAt ?? x.lastSyncAt) : (x.dataAsOf ?? x.lastSyncAt);
  const stale =
    !!x.syncError ||
    (withToday && (asOf == null || now - asOf > META_SLA_MS)) ||
    (x.incompleteDays?.length ?? 0) > 0;
  return [{ source: 'meta', latency: 'near_realtime', fetchedAt: now, dataAsOf: asOf ?? now, stale }];
}

/** Período só de hoje cuja última leitura de hoje é de antes da meia-noite: zero aqui não é gasto zero. */
export function noTodayYet(now: number, x: MetaSyncFields, r: { from: number; to: number }): boolean {
  const todayStart = dayStart(todayKey(now));
  if (r.from < todayStart) return false;
  const at = x.todayAt ?? x.lastSyncAt;
  return at == null || at < todayStart;
}

/**
 * Por que a mídia de um período não vale como número real: Meta com erro, não conectada ou hoje
 * ainda sem leitura (aí "R$ 0" de gasto seria falso). `null` = a mídia serve. Para quem desconta a
 * mídia de um resultado (DRE, canais): com motivo, a linha e o resultado ficam sem valor.
 */
export function mediaMissing(
  load: RealCtx['loadMedia'],
  m: RealMedia | null,
  now: number,
  r: { from: number; to: number },
): string | null {
  if (!m) return gapText(mediaGap(load, r.from, r.to));
  return noTodayYet(now, m, r) ? META_GAP.noToday : null;
}

/**
 * Situação real da Meta (rodapé, selo "Ao vivo" e cartão da integração), a partir da leitura da mídia
 * da última hora (`load(now − 1 h, now)`). Nunca a da simulação: não conectada; com erro (e a
 * mensagem) quando a leitura ou a última sincronização falhou; atrasada quando hoje está sem leitura
 * há mais que o SLA (35 min). Contas, cota e eventos simulados saem.
 */
export function metaIntegrationStatus(
  s: IntegrationStatus,
  m: RealMedia | null,
  gap: MediaGap | null,
  now: number,
): IntegrationStatus {
  const { accounts: _a, quota: _q, eventsSpark: _e, lastError: _l, ...rest } = s;
  const base: IntegrationStatus = {
    ...rest,
    label: 'Meta Ads',
    slaMs: META_SLA_MS,
    eventsLastHour: null,
  };
  if (!m) {
    if (gap?.kind === 'not_configured')
      return { ...base, state: 'not_configured', lastSyncAt: null, lagMs: null };
    return {
      ...base,
      state: 'error',
      lastSyncAt: null,
      lagMs: null,
      lastError: { at: now, code: 'source_unavailable', message: gap?.message ?? META_GAP.error },
    };
  }
  const at = m.todayAt ?? m.lastSyncAt;
  const lag = at == null ? null : Math.max(0, now - at);
  const out: IntegrationStatus = {
    ...base,
    state: lag == null || lag > META_SLA_MS ? 'degraded' : 'connected',
    lastSyncAt: m.lastSyncAt,
    lagMs: lag,
  };
  if (m.syncError)
    return {
      ...out,
      state: 'error',
      lastError: { at: m.lastSyncAt ?? now, code: 'sync_failed', message: m.syncError },
    };
  return out;
}

/** Situação real da Meta já com a leitura da última hora (o que o rodapé e o `status()` precisam). */
export async function loadMetaStatus(
  load: RealCtx['loadMedia'],
  s: IntegrationStatus,
  now: number,
): Promise<IntegrationStatus> {
  const from = now - HOUR;
  const m = await load(from, now).catch(() => null);
  return metaIntegrationStatus(s, m, mediaGap(load, from, now), now);
}

/** Custo real da mídia como o servidor calcula; servidor antigo: gasto ÷ (1 − imposto da Ajustes). */
export const mediaCostOf = (x: RealMedia, metaTaxPct: number): number =>
  x.totals.costCents ?? Math.round(x.totals.spendCents / (1 - metaTaxPct));

/** Compras e valor que a Meta atribui aos anúncios (Gerenciador); servidor antigo: as da Kiwify pelo link. */
export const adPurchases = (x: RealMedia): number => x.meta?.purchases ?? x.sales.front;
export const adValueCents = (x: RealMedia): number => x.meta?.valueCents ?? x.sales.grossCents;
/**
 * Compras do curso que a Meta atribui (as do 20X separadas pelo valor): a base do CPA, porque o teto
 * do CPA já soma o 20X por comprador do curso. Servidor sem a separação: todas as compras.
 */
export const adCourseSales = (x: RealMedia): number => Math.max(0, adPurchases(x) - (x.meta?.upsell ?? 0));
export const ROAS_NOTE =
  'no período; valor das compras que a Meta atribui aos anúncios ÷ valor gasto na Meta';
export const CPA_NOTE =
  'no período; investimento ÷ compras do curso que a Meta atribui (o 20X separado pelo valor)';
/** Recorte por campanha, conjunto ou anúncio: a Kiwify não separa as vendas por campanha. */
export const ENTITY_SALES_NOTE =
  'sem valor neste recorte: as vendas da Kiwify não vêm separadas por campanha (veja o ROAS e o CPA da Meta)';
/** Recorte por campanha, conjunto ou anúncio por hora: a Meta só dá a hora da conta inteira. */
export const ENTITY_HOURLY_NOTE =
  'Por hora, a Meta só informa a conta inteira. Para ver este item, escolha 7D ou mais.';
/** Só filtro de conta, por hora: a Meta dá o gasto por hora da conta, mas não as compras nem as visitas. */
export const ACCOUNT_HOURLY_NOTE =
  'Por hora, a Meta não informa compras nem visitas. Para ver este item, escolha 7D ou mais.';
/** Vendas de um recorte por campanha pelo que a Meta atribui (regra do dono: venda de anúncio conta pela Meta). */
export const ENTITY_META_SALES_NOTE =
  'compras que a Meta atribui a este recorte (as do Gerenciador), não a Kiwify';

/** Só filtro de conta (sem campanha, conjunto nem anúncio): a Meta dá o gasto por hora da conta. */
export const accountOnly = (f: Filters): boolean =>
  !!f.accountIds?.length && !f.campaignIds?.length && !f.adsetIds?.length && !f.adIds?.length;

/**
 * Gasto por hora só das contas pedidas (a soma delas, hora a hora). Servidor antigo, sem a separação
 * por conta: `null` (a tela diz que não há a hora, nunca mostra a da conta inteira).
 */
export function accountHourly(
  x: Pick<RealMedia, 'hourlyByAccount'>,
  ids: readonly string[],
): RealMedia['hourly'] | null {
  if (!x.hourlyByAccount) return null;
  const by = new Map<number, { spendCents: number; costCents: number }>();
  for (const id of ids)
    for (const p of x.hourlyByAccount[id] ?? []) {
      const cur = by.get(p.t) ?? { spendCents: 0, costCents: 0 };
      cur.spendCents += p.spendCents;
      cur.costCents += p.costCents ?? p.spendCents;
      by.set(p.t, cur);
    }
  return [...by].sort((a, b) => a[0] - b[0]).map(([t, v]) => ({ t, ...v }));
}

/**
 * Soma das horas em [from, to) (a hora em andamento pela parte já passada): o gasto de uma conta em
 * "ontem até a mesma hora".
 */
export function hoursUntil(
  hourly: RealMedia['hourly'],
  from: number,
  to: number,
  pick: (p: RealMedia['hourly'][number]) => number,
): number {
  let sum = 0;
  for (const p of hourly) {
    const share = Math.max(0, Math.min(to, p.t + HOUR) - Math.max(from, p.t)) / HOUR;
    if (share > 0) sum += pick(p) * share;
  }
  return Math.round(sum);
}

/** Comparação "ontem até a mesma hora" de um número que precisa das compras (a Meta não dá compras por hora). */
export const CUT_NOTE = 'sem comparação no mesmo horário: a Meta não informa compras nem visitas por hora';
/** Comparação "até a mesma hora" sem o detalhe por hora daquele dia. */
export const CUT_MISSING_NOTE = 'sem comparação no mesmo horário: falta o detalhe por hora daquele dia';

/**
 * Série do modo ao vivo com o motivo de não ter linha (fora do contrato): a tela mostra o texto no
 * lugar de um gráfico vazio. Lido com `liveSeriesNote`.
 */
export type LiveSeries = ColumnarSeries & { unmeasured?: string };
export const liveSeriesNote = (s: ColumnarSeries | null | undefined): string | null =>
  (s as LiveSeries | null | undefined)?.unmeasured ?? null;

/** Filtro que a Meta não recorta (pagamento, produto, origem...): o gasto é da conta inteira. */
export const OFF_META_NOTE = 'sem valor com este filtro: a Meta não recorta o gasto por ele';
export const ACCOUNT_NOTE = 'conta inteira: a Meta não recorta este filtro';

/** KPIs de mídia: com a Meta real; sem ela, sem valor e com o motivo (nunca os da simulação). */
export const MEDIA_KPI_IDS = [
  'spendAm',
  'mediaCost',
  'impressions',
  'cpm',
  'linkClicks',
  'ctrLink',
  'cpcLink',
  'lpv',
  'connectRate',
  'cpa',
  'roas',
  'mer',
  'roasReal',
  'profit',
  'margin',
  'roi',
  'cpaBlended',
  'profitOperating',
  'roasFront',
  'epc',
] as const satisfies readonly KpiId[];

/**
 * KPIs de mídia sem conta real: sem valor e com o motivo (antes passavam da simulação para o FIN-02 e a
 * paleta). A Meta não diz o produto de cada compra (o valor do curso sozinho não existe) e a receita
 * por clique misturaria as vendas de todas as origens com os cliques dos anúncios.
 */
const NOT_MEASURED: Readonly<Partial<Record<KpiId, string>>> = {
  roasFront: 'não medido: a Meta não separa o valor do curso do valor do 20X (veja o ROAS)',
  epc: 'não medido: a Meta não separa as vendas por clique (veja o ROAS e o custo por clique)',
};

/** Misturam vendas e gasto: com filtro fora da Meta ficam sem valor (vendas recortadas ÷ gasto inteiro). */
const MIXED_KPIS: ReadonlySet<KpiId> = new Set<KpiId>([
  'cpa',
  'mer',
  'roasReal',
  'profit',
  'margin',
  'cpaBlended',
  'profitOperating',
]);
/** Séries que o recorte por campanha monta com a Meta (o resto, com `kpi`, em `entitySeries`). */
const ENTITY_SERIES_KEYS: ReadonlySet<string> = new Set([
  'spendByHour',
  'spendByDay',
  'revenueByHour',
  'revenueByDay',
  'revenueCumulative',
  'salesByHour',
  'upsellByHour',
  'cpaCumulative',
  'cpaByHour',
  'profitCumulative',
  'profitByDay',
]);
/** Lucro por campanha precisaria das vendas da Kiwify por campanha: no recorte, sem linha. */
const ENTITY_PROFIT_KEYS: ReadonlySet<string> = new Set(['profitCumulative', 'profitByDay']);
/**
 * KPIs dos pedidos da Kiwify (os `ORDER_KPIS` do núcleo, em `realSales`). A Kiwify não separa os
 * pedidos por campanha: num recorte por conta, campanha, conjunto ou anúncio eles ficam sem valor (antes
 * passavam com a conta inteira e o chip do recorte ativo), menos as vendas do curso e do 20X, que saem
 * do que a Meta atribui ao recorte.
 */
const ENTITY_ORDER_KPIS: readonly KpiId[] = [
  'revenueGross',
  'revenueNet',
  'frontSales',
  'upsellSales',
  'aov',
  'interestPaid',
  'pendingRevenue',
  'refundsAmount',
  'refundsCount',
  'upsellTakeRate',
  'cardApprovalAttempt',
  'cardApprovalBuyer',
  'pixPaidRate',
  'cartsAbandoned',
  'revenueFinal',
  'pixMedianMin',
];
/**
 * Vendas e faturamento de um recorte por campanha pelo que a Meta atribui (regra do dono: venda de
 * anúncio conta pela Meta): as do curso, as do 20X (separadas pelo valor) e o valor das compras.
 */
const ENTITY_ORDER_PICK: Partial<Record<KpiId, (d: RealInsightDay) => number>> = {
  frontSales: (d) => d.metaPurchases - d.metaUpsell,
  upsellSales: (d) => d.metaUpsell,
};
/** Campo da linha da tabela com o valor de cada campanha no detalhe de um KPI de mídia. */
const BY_CAMPAIGN: Partial<Record<KpiId, keyof InsightRow>> = {
  spendAm: 'spendAm',
  mediaCost: 'mediaCost',
  impressions: 'impressions',
  cpm: 'cpm',
  linkClicks: 'linkClicks',
  ctrLink: 'ctrLink',
  cpcLink: 'cpcLink',
  lpv: 'lpv',
  connectRate: 'connectRate',
  cpa: 'cpa',
  roas: 'roas',
  roi: 'roi',
};
/** Dependem das compras ou das visitas da Meta, que não existem por hora: sem comparação "até a mesma hora". */
const CUT_PURCHASE_KPIS: ReadonlySet<KpiId> = new Set<KpiId>(['cpa', 'roas', 'roi', 'lpv', 'connectRate']);
/** Fim de um dia em São Paulo (meia-noite): o intervalo fecha em dias inteiros. */
const wholeDay = (t: number): boolean => (t - BRT) % DAY === 0;
/** Sem série (a fonte não tem o dado nesse balde): a tela mostra o vazio, nunca a simulação. */
const NO_SERIES: Omit<ColumnarSeries, 'freshness'> = { t: [], series: [], markers: [] };
/** Por dia, precisam das vendas reais do dia (Kiwify). */
const SALES_DAY_KPIS: ReadonlySet<KpiId> = new Set<KpiId>([
  'mer',
  'roasReal',
  'profit',
  'margin',
  'cpaBlended',
  'profitOperating',
]);

/**
 * Formato e régua que mudam com a conta do modo ao vivo. O ROI passa a ser valor ÷ custo de mídia, em
 * vezes ("2,89", como o ROAS e a coluna da tabela), não mais lucro ÷ custo em %: o detalhe e o gráfico
 * dia a dia saem no mesmo formato. A régua antiga (alvo do ROI, sobre o lucro) não vale nessa base e
 * sai, sem linhas de referência na escala errada.
 */
const LIVE_KPI_SHAPE: Readonly<Partial<Record<KpiId, Partial<Pick<KpiDef, 'format' | 'thresholds'>>>>> = {
  roi: { format: 'ratio2', thresholds: { kind: 'none' } },
};

/** Troca nome, ajuda, fórmula (e o formato do ROI) dos KPIs de mídia pelos do modo ao vivo (`kpiDefs` do boot). */
export function withLiveKpiTexts<B>(boot: B): B {
  const defs = (boot as { kpiDefs?: Record<string, object> }).kpiDefs;
  if (!defs) return boot;
  const next = { ...defs };
  for (const [id, text] of Object.entries(LIVE_KPI_TEXTS)) {
    const d = next[id];
    if (d && text) next[id] = { ...d, ...text };
  }
  for (const [id, shape] of Object.entries(LIVE_KPI_SHAPE)) {
    const d = next[id];
    if (d && shape) next[id] = { ...d, ...shape };
  }
  return { ...boot, kpiDefs: next };
}

/**
 * Código do criativo como a tela dos Criativos agrupa (`codeOf`: no modo ao vivo os anúncios não têm
 * código cadastrado, então vale o começo do nome, "V07 · …", ou o nome inteiro, "AERO 07").
 */
export function creativeCode(row: Pick<InsightRow, 'id' | 'name'>): string {
  const first = row.name.trim().split(' ')[0] ?? '';
  return /^[A-Z]+\d+[A-Z]?$/.test(first) ? first : row.name.trim() || row.id;
}

/**
 * Alcance e frequência de um criativo com mais de um anúncio: sem valor. A média das frequências pelas
 * impressões não é a frequência do criativo (os anúncios alcançam pessoas repetidas) e, em 7D, só os
 * anúncios de um dia trazem o alcance (o sheet mostrava a frequência de uma parte pequena). Criativo de
 * um anúncio só: o alcance que a Meta deu para ele.
 */
export function groupFrequency(rows: readonly InsightRow[]): InsightRow[] {
  const count = new Map<string, number>();
  for (const r of rows) count.set(creativeCode(r), (count.get(creativeCode(r)) ?? 0) + 1);
  return rows.map((r) =>
    (count.get(creativeCode(r)) ?? 0) > 1 ? { ...r, reach: null, frequency: null } : r,
  );
}

export function mediaModule(ctx: RealCtx): RealModule {
  const { clock } = ctx;
  /** Despesas fixas cadastradas (Ajustes › Custos) no intervalo, pelo rateio diário (o mesmo da DRE). */
  const fixedIn = (from: number, to: number) => fixedCostsIn(ctx.settings()?.costs?.expenses ?? [], from, to);

  /** Todos os KPIs de mídia sem valor, com o motivo (o `{...snap.kpis, ...kpis}` não traz a simulação de volta). */
  const noMedia = (motivo: string): Partial<Record<KpiId, KpiValue>> =>
    Object.fromEntries(MEDIA_KPI_IDS.map((id) => [id, noRealKpi(id, motivo)]));

  /**
   * Recorte por conta, campanha, conjunto ou anúncio: os KPIs dos pedidos da Kiwify ficam sem valor (a
   * Kiwify não separa os pedidos por campanha; antes ficava a conta inteira com o chip do recorte). As
   * vendas do curso e do 20X saem do que a Meta atribui ao recorte (regra do dono), com a nota; sem a
   * Meta, sem valor também. `mc` = o recorte no período de comparação (só com dias inteiros).
   */
  function entityOrders(
    out: Partial<Record<KpiId, KpiValue>>,
    m: RealMedia | null,
    mc: RealMedia | null,
    now: number,
    fresh: Freshness[] | null,
  ): Partial<Record<KpiId, KpiValue>> {
    for (const id of ENTITY_ORDER_KPIS) out[id] = noRealKpi(id, ENTITY_SALES_NOTE);
    const meta = m?.meta;
    if (!meta || !fresh) return out;
    const course = (x: RealMedia | null) =>
      x?.meta ? Math.max(0, x.meta.purchases - (x.meta.upsell ?? 0)) : null;
    const up = (x: RealMedia | null) => x?.meta?.upsell ?? null;
    const put = (id: KpiId, v: number | null, c: number | null) => {
      if (v == null) return;
      out[id] = { ...kv(id, v, c, now), freshness: fresh, note: ENTITY_META_SALES_NOTE };
    };
    put('frontSales', course(m), mc ? course(mc) : null);
    put('upsellSales', up(m), mc ? up(mc) : null);
    return out;
  }

  async function kpis({ q, now, r, cr, size, t, c }: RealKpiInput) {
    const [m0, mc0] = await Promise.all([
      ctx.loadMedia(r.from, r.to),
      cr ? ctx.loadMedia(cr.from, cr.to) : Promise.resolve(null),
    ]);
    // Mídia real (Meta Ads). Sem ela (erro ou não conectada) ou hoje ainda sem leitura da Meta (gasto
    // "R$ 0" e lucro = faturamento inteiro seriam falsos), os cartões dizem o motivo.
    const missing = mediaMissing(ctx.loadMedia, m0, now, r);
    const entity = hasEntityFilter(q.filters);
    if (missing || !m0) {
      const out = noMedia(missing ?? META_GAP.error);
      return entity ? entityOrders(out, null, null, now, null) : out;
    }
    // Recorte por conta, campanha, conjunto ou anúncio: os números são só das linhas da Meta que passam
    // no filtro (antes: a conta inteira com a nota "no recorte"). A comparação só com dias inteiros (a
    // Meta não dá a hora de uma campanha); só filtro de conta: o gasto também por hora, o da conta.
    let m = m0;
    let mc = mc0;
    const acctIds = entity && accountOnly(q.filters) ? (q.filters.accountIds ?? []) : null;
    const acct = acctIds ? accountHourly(m0, acctIds) : null;
    const acctC = acctIds && mc0 ? accountHourly(mc0, acctIds) : null;
    if (entity) {
      try {
        m = { ...asMedia(m0, await entityRows(r, q.filters)), hourly: acct ?? [] };
        mc = mc0 && cr && wholeDay(cr.to) ? asMedia(mc0, await entityRows(cr, q.filters)) : null;
      } catch (e) {
        return entityOrders(noMedia(errorText(e)), null, null, now, null);
      }
    }
    const costOf = ctx.costOf;
    const spend = (x: RealMedia | null) => x?.totals.spendCents ?? null;
    const ratio = (a: number | null | undefined, b: number | null | undefined, k = 1) =>
      a != null && b ? (a / b) * k : null;
    const hourS = (hourly: RealMedia['hourly'], from: number, end: number, cut: number) =>
      buckets(
        hourly.map((p) => ({ ...row0, at: p.t, grossCents: p.spendCents })),
        from,
        end,
        size,
        cut,
        (o) => o.grossCents,
      ).values;
    // Hora do gasto: a da conta inteira, ou a das contas do filtro; campanha, conjunto e anúncio não têm.
    const hours = !entity ? m.hourly : acct;
    const hoursC = !entity ? (mc?.hourly ?? null) : acctC;
    const spark = size === HOUR && hours ? hourS(hours, r.from, r.end, now) : undefined;
    const sparkC =
      size === HOUR && hoursC && cr
        ? hourS(hoursC, cr.from, cr.end ?? cr.from + (r.end - r.from), cr.to)
        : undefined;
    // Filtro de conta em "ontem até a mesma hora": o gasto e o custo da conta cortados pela hora (a
    // comparação do recorte pelas linhas por dia só vale com dias inteiros).
    const acctCut = (pick: (p: RealMedia['hourly'][number]) => number): number | null =>
      !mc && acctC && cr && mc0?.cut?.byHour ? hoursUntil(acctC, cr.from, cr.to, pick) : null;
    // Vendas de anúncio pelo que a Meta marcou (Gerenciador); servidor antigo: as da Kiwify pelo link.
    const out: Partial<Record<KpiId, KpiValue>> = {
      spendAm: kv('spendAm', spend(m), mc ? spend(mc) : acctCut((p) => p.spendCents), now, spark, sparkC),
      mediaCost: kv(
        'mediaCost',
        costOf(m),
        mc ? costOf(mc) : acctCut((p) => p.costCents ?? p.spendCents),
        now,
      ),
      impressions: kv('impressions', m.totals.impressions, mc?.totals.impressions ?? null, now),
      cpm: kv(
        'cpm',
        ratio(m.totals.spendCents, m.totals.impressions, 1000),
        mc ? ratio(mc.totals.spendCents, mc.totals.impressions, 1000) : null,
        now,
      ),
      linkClicks: kv('linkClicks', m.totals.linkClicks, mc?.totals.linkClicks ?? null, now),
      ctrLink: kv(
        'ctrLink',
        ratio(m.totals.linkClicks, m.totals.impressions),
        mc ? ratio(mc.totals.linkClicks, mc.totals.impressions) : null,
        now,
      ),
      cpcLink: kv(
        'cpcLink',
        ratio(m.totals.spendCents, m.totals.linkClicks),
        mc ? ratio(mc.totals.spendCents, mc.totals.linkClicks) : null,
        now,
      ),
      lpv: kv('lpv', m.totals.lpv, mc?.totals.lpv ?? null, now),
      connectRate: kv(
        'connectRate',
        ratio(m.totals.lpv, m.totals.linkClicks),
        mc ? ratio(mc.totals.lpv, mc.totals.linkClicks) : null,
        now,
      ),
      // CPA: investimento ÷ compras do curso que a Meta atribui (o 20X separado pelo valor).
      cpa: kv(
        'cpa',
        ratio(m.totals.spendCents, adCourseSales(m)),
        mc ? ratio(mc.totals.spendCents, adCourseSales(mc)) : null,
        now,
      ),
      // ROAS: valor das compras da Meta ÷ valor gasto (sem imposto nem IOF), o do Gerenciador.
      roas: kv(
        'roas',
        ratio(adValueCents(m), m.totals.spendCents),
        mc ? ratio(adValueCents(mc), mc.totals.spendCents) : null,
        now,
      ),
      // ROI (nome do dono): valor das compras da Meta ÷ custo de mídia (com impostos, câmbio e IOF).
      roi: kv('roi', ratio(adValueCents(m), costOf(m)), mc ? ratio(adValueCents(mc), costOf(mc)) : null, now),
      // MER, retorno real, lucro e margem: as mesmas contas da DRE (faturamento = valor dos produtos;
      // a mídia com imposto). O anúncio sai do faturamento inteiro, não só da comissão.
      mer: kv('mer', ratio(t.gross, costOf(m)), mc && c ? ratio(c.gross, costOf(mc)) : null, now),
      roasReal: kv(
        'roasReal',
        ratio(finalOf(t), costOf(m)),
        mc && c ? ratio(finalOf(c), costOf(mc)) : null,
        now,
      ),
      profit: kv('profit', finalOf(t) - costOf(m), mc && c ? finalOf(c) - costOf(mc) : null, now),
      margin: kv(
        'margin',
        t.gross ? (finalOf(t) - costOf(m)) / t.gross : null,
        mc && c?.gross ? (finalOf(c) - costOf(mc)) / c.gross : null,
        now,
      ),
      // CPA geral (FIN-02): investimento ÷ todas as vendas do curso (Kiwify, de todas as origens).
      cpaBlended: kv(
        'cpaBlended',
        ratio(m.totals.spendCents, t.front),
        mc && c ? ratio(mc.totals.spendCents, c.front) : null,
        now,
      ),
      // Resultado depois das despesas fixas: o lucro menos o rateio das despesas cadastradas (a mesma
      // linha da DRE, sem o imposto sobre a receita, que o lucro também não desconta).
      profitOperating: kv(
        'profitOperating',
        finalOf(t) - costOf(m) - fixedIn(r.from, r.to),
        mc && c && cr ? finalOf(c) - costOf(mc) - fixedIn(cr.from, cr.to) : null,
        now,
      ),
    };
    // Filtro que a Meta não recorta (Pix, produto, origem...): vendas recortadas contra o gasto da
    // conta inteira dariam MER, lucro e CPA falsos. Esses ficam sem valor; os só da Meta dizem que são
    // da conta inteira.
    const off = offMetaDims(q.filters).length > 0;
    const fresh = metaFreshness(now, m, r);
    // "Ontem até a mesma hora" (dia passado cortado no meio): o gasto, as impressões e os cliques vêm
    // cortados pela hora; compras, valor e visitas não têm hora, então quem depende deles fica sem
    // comparação (antes: o dia inteiro de ontem contra hoje até agora). Sem o detalhe por hora, nada
    // de mídia compara.
    const cut = mc?.cut ?? null;
    const noCompare: ReadonlySet<KpiId> = cut
      ? cut.byHour
        ? CUT_PURCHASE_KPIS
        : new Set<KpiId>(MEDIA_KPI_IDS)
      : new Set<KpiId>();
    for (const id of MEDIA_KPI_IDS) {
      const not = NOT_MEASURED[id];
      if (not) {
        out[id] = noRealKpi(id, not);
        continue;
      }
      if (off && MIXED_KPIS.has(id)) {
        out[id] = noRealKpi(id, OFF_META_NOTE);
        continue;
      }
      // Recorte por campanha: as vendas da Kiwify não vêm por campanha (seria a conta inteira).
      if (entity && SALES_DAY_KPIS.has(id)) {
        out[id] = noRealKpi(id, ENTITY_SALES_NOTE);
        continue;
      }
      const v = out[id];
      if (!v) continue;
      v.freshness = fresh;
      if (noCompare.has(id) && v.compareValue != null) {
        v.compareValue = null;
        v.delta = null;
        delete v.sparkCompare;
        v.note = cut?.byHour ? CUT_NOTE : CUT_MISSING_NOTE;
      }
      if (m.syncError) v.note = `${META_GAP.error} (números da última leitura)`;
      else if (off) v.note = ACCOUNT_NOTE;
    }
    return entity ? entityOrders(out, m, mc, now, fresh) : out;
  }

  /**
   * Tabela da Meta no nível pedido, recortada pelo filtro de conta, campanha, conjunto e anúncio. Filtro
   * mais fino que o nível (anúncio na visão por campanha): a campanha soma só os anúncios do filtro.
   */
  async function cutTable(
    r: { from: number; to: number },
    level: 'campaign' | 'adset' | 'ad',
    f: Filters,
  ): Promise<RealInsights> {
    const need = cutLevel(level, f);
    if (need === level || level === 'ad') return realTable(r, level);
    const [fine, own] = await Promise.all([realTable(r, need), realTable(r, level)]);
    return { ...own, rows: rollUp(filterRows(fine.rows, f), level, own.rows) };
  }

  /** Linhas da Meta que passam no recorte (no nível em que o filtro dá para cortar), sem repetir gasto. */
  async function entityRows(r: { from: number; to: number }, f: Filters): Promise<RealInsightRow[]> {
    const real = await realTable(r, cutLevel('campaign', f));
    return filterRows(real.rows, f);
  }

  /**
   * Mídia de um recorte (as linhas da Meta que passam no filtro) no formato da mídia da conta: as
   * mesmas contas dos cartões valem para a campanha. Sem a hora (a Meta não dá hora por campanha).
   */
  function asMedia(base: RealMedia, rows: readonly RealInsightRow[]): RealMedia {
    // A janela de clique é da conta inteira (não vem por anúncio): no recorte ela fica sem número.
    const { cut: _cut, metaClick: _click, ...rest } = base;
    const sum = (pick: (r: RealInsightRow) => number) => rows.reduce((a, r) => a + pick(r), 0);
    const tax = ctx.metaTaxPct();
    const spend = sum((r) => r.spendCents);
    const withMeta = rows.some((r) => r.metaPurchases != null);
    return {
      ...rest,
      totals: {
        spendCents: spend,
        costCents: sum((r) => r.costCents ?? Math.round(r.spendCents / (1 - tax))),
        impressions: sum((r) => r.impressions),
        reach: 0,
        clicks: sum((r) => r.clicks),
        linkClicks: sum((r) => r.linkClicks),
        lpv: sum((r) => r.lpv),
      },
      sales: {
        paid: sum((r) => r.frontSales + r.upsellSales),
        front: sum((r) => r.frontSales),
        grossCents: sum((r) => r.grossCents),
        commissionCents: sum((r) => r.commissionCents),
        unmatched: 0,
      },
      ...(withMeta || rows.length === 0
        ? {
            meta: {
              purchases: sum((r) => r.metaPurchases ?? 0),
              valueCents: sum((r) => r.metaValueCents ?? 0),
              upsell: sum((r) => r.metaUpsell ?? 0),
            },
          }
        : {}),
      hourly: [],
      daily: [],
    };
  }

  /** Sem mídia real: o erro da fonte, para a tela mostrar "Tentar de novo" (nunca a simulação). */
  const unavailable = (from: number, to: number) =>
    metaUnavailable(gapText(mediaGap(ctx.loadMedia, from, to)));

  async function series(q: SeriesQuery): Promise<ColumnarSeries | undefined> {
    // Recorte por conta, campanha, conjunto ou anúncio: a série é a do próprio recorte (nunca a conta
    // inteira nem a simulação).
    // Só filtro de conta, gasto por hora: a hora da própria conta (a Meta dá a hora por conta).
    const kpiSpend =
      (q.key === 'kpiByHour' || q.key === 'kpiByDay') && (q.kpi === 'spendAm' || q.kpi === 'mediaCost');
    const acctIds = accountOnly(q.filters) ? (q.filters.accountIds ?? []) : null;
    const acctHour =
      acctIds != null &&
      (q.key === 'spendByHour' || (q.key === 'kpiByHour' && kpiSpend)) &&
      q.bucket !== '1d';
    if (hasEntityFilter(q.filters) && !acctHour) {
      const s = await entitySeries(q);
      if (s) return s;
    }
    // O detalhe de Investimento e de Custo de mídia (kpiByHour/kpiByDay) usa o gasto real por hora
    // e por dia, a mesma série do Tráfego (antes caía na simulação e só a ponta era real).
    if (q.key === 'spendByHour' || q.key === 'spendByDay' || kpiSpend) {
      const useCost = q.kpi === 'mediaCost';
      const now = clock();
      const r = rangeMs(q, now);
      const cr = compareMs(q, now);
      const [m, mc] = await Promise.all([
        ctx.loadMedia(r.from, r.to),
        cr ? ctx.loadMedia(cr.from, cr.to) : Promise.resolve(null),
      ]);
      if (!m) throw unavailable(r.from, r.to);
      if (noTodayYet(now, m, r)) throw metaUnavailable(META_GAP.noToday);
      // Servidor antigo, sem a hora por conta: o eixo sem linha e o motivo (nunca a conta inteira).
      if (acctHour && !m.hourlyByAccount) return entitySeries(q);
      const hoursOf = (x: RealMedia) => (acctIds && acctHour ? (accountHourly(x, acctIds) ?? []) : x.hourly);
      const size = q.key === 'spendByDay' || q.key === 'kpiByDay' || q.bucket === '1d' ? DAY : HOUR;
      const val = (p: { spendCents: number; costCents?: number }) =>
        useCost ? (p.costCents ?? p.spendCents) : p.spendCents;
      const pts = (x: RealMedia) =>
        size === DAY
          ? x.daily.map((d) => ({
              ...row0,
              at: Date.parse(`${d.date}T00:00:00Z`) + BRT,
              grossCents: val(d),
            }))
          : hoursOf(x).map((p) => ({ ...row0, at: p.t, grossCents: val(p) }));
      const main = buckets(pts(m), r.from, r.end, size, now, (o) => o.grossCents);
      const out: ColumnarSeries = {
        t: main.t,
        series: [
          {
            key: kpiSpend ? (q.kpi as string) : 'spend',
            label: useCost ? 'Custo de mídia' : 'Investimento',
            role: 'primary',
            values: main.values,
          },
        ],
        markers: [],
        freshness: metaFreshness(now, m, r),
      };
      if (mc && cr)
        out.series.push({
          key: 'spend_cmp',
          label: 'Comparação',
          role: 'compare',
          values: buckets(pts(mc), cr.from, cr.end, size, Number.POSITIVE_INFINITY, (o) => o.grossCents)
            .values,
        });
      // Investimento × faturamento por hora: o faturamento real (todas as vendas pagas) vai na mesma
      // série, como o gráfico do Tráfego espera. Filtro de conta: a Kiwify não separa as vendas por
      // conta, então sem a linha do faturamento (nunca o da conta inteira).
      if (size === HOUR && !kpiSpend && !acctHour) {
        const paid = (await ctx.load(r.from, r.to)).filter((o) => isPaid(o) && matches(o, q));
        out.series.push({
          key: 'revenue',
          label: 'Faturamento',
          role: 'split',
          values: buckets(paid, r.from, r.end, size, now, revenueOf).values,
        });
      }
      return out;
    }
    // KPI sem conta real: sem série, nunca a da simulação.
    if ((q.key === 'kpiByHour' || q.key === 'kpiByDay') && q.kpi && NOT_MEASURED[q.kpi]) {
      const now = clock();
      const r = rangeMs(q, now);
      const m = await ctx.loadMedia(r.from, r.to);
      if (!m) throw unavailable(r.from, r.to);
      return { ...NO_SERIES, freshness: metaFreshness(now, m, r) };
    }
    // CPA geral por hora: gasto acumulado ÷ vendas do curso acumuladas (todas as origens), as duas
    // reais por hora (o gasto da Meta e os pedidos da Kiwify).
    if (q.key === 'kpiByHour' && q.kpi === 'cpaBlended') return blendedByHour(q);
    // A Meta não informa compras (nem visitas) por hora: o CPA acumulado por hora e o detalhe por hora
    // dos KPIs de mídia (menos o gasto) ficam sem série, nunca a simulação nem o gasto ÷ vendas da
    // Kiwify de todos os canais (que contradizia o CPA da Meta do topo).
    if (
      q.key === 'cpaCumulative' ||
      (q.key === 'kpiByHour' && q.kpi && (MEDIA_KPI_IDS as readonly KpiId[]).includes(q.kpi))
    ) {
      const now = clock();
      const r = rangeMs(q, now);
      const m = await ctx.loadMedia(r.from, r.to);
      if (!m) throw unavailable(r.from, r.to);
      return { ...NO_SERIES, freshness: metaFreshness(now, m, r) };
    }
    // Dia a dia: a mesma conta do número do topo, aplicada a cada dia (CPA = gasto do dia ÷ compras do
    // curso que a Meta atribui no dia; ROAS = valor da Meta ÷ gasto; MER e lucro com as vendas reais).
    if (q.key === 'kpiByDay' && q.kpi && (MEDIA_KPI_IDS as readonly KpiId[]).includes(q.kpi))
      return dailyKpi(q, q.kpi);
    return undefined;
  }

  /**
   * Série de um recorte por conta, campanha, conjunto ou anúncio, dia a dia, com as linhas da Meta que
   * passam no filtro: investimento, custo, CPA, ROAS, CTR..., e as vendas e o valor pelo que a Meta
   * atribui (regra do dono). A Meta não dá a hora de uma campanha: por hora, eixo sem linha e o motivo
   * (`unmeasured`), nunca a curva da conta. MER, lucro e afins precisariam das vendas da Kiwify por
   * campanha: sem linha. Outras séries: `undefined` (não é com a mídia).
   */
  async function entitySeries(q: SeriesQuery): Promise<LiveSeries | undefined> {
    const kpi = q.key === 'kpiByDay' || q.key === 'kpiByHour' ? q.kpi : undefined;
    const known =
      ENTITY_SERIES_KEYS.has(q.key) ||
      (kpi != null && ((MEDIA_KPI_IDS as readonly KpiId[]).includes(kpi) || ENTITY_ORDER_KPIS.includes(kpi)));
    if (!known) return undefined;
    const now = clock();
    const r = rangeMs(q, now);
    const cr = compareMs(q, now);
    const m = await ctx.loadMedia(r.from, r.to);
    if (!m) throw unavailable(r.from, r.to);
    if (noTodayYet(now, m, r)) throw metaUnavailable(META_GAP.noToday);
    const fresh = metaFreshness(now, m, r);
    const daily =
      q.key === 'kpiByDay' ||
      q.key === 'spendByDay' ||
      q.key === 'revenueByDay' ||
      q.key === 'profitByDay' ||
      ((q.key === 'revenueCumulative' || q.key === 'profitCumulative') && q.bucket === '1d');
    const key =
      kpi ?? (q.key.startsWith('spend') ? 'spend' : q.key.startsWith('revenue') ? 'revenue' : q.key);
    const label =
      !kpi && q.key.startsWith('spend')
        ? 'Investimento'
        : !kpi && q.key.startsWith('revenue')
          ? 'Valor das compras (Meta)'
          : 'No período';
    // O CPA acumulado sem linha nenhuma (não uma linha toda vazia): a sparkline das "Campanhas ao
    // vivo" some em vez de desenhar um espaço vazio com o rótulo "CPA acumulado".
    const blank = (size: number, note: string): LiveSeries => {
      const axis = buckets([], r.from, r.end, size, now, () => 0);
      return {
        t: axis.t,
        series:
          q.key === 'cpaCumulative' ? [] : [{ key, label, role: 'primary', values: axis.t.map(() => null) }],
        markers: [],
        freshness: fresh,
        unmeasured: note,
      };
    };
    // Precisam das vendas da Kiwify por campanha (que não existem): sem linha, por dia ou por hora.
    const salesOnly =
      ENTITY_PROFIT_KEYS.has(q.key) ||
      (kpi != null &&
        (NOT_MEASURED[kpi] != null ||
          SALES_DAY_KPIS.has(kpi) ||
          (ENTITY_ORDER_KPIS.includes(kpi) && ENTITY_ORDER_PICK[kpi] == null)));
    if (salesOnly) return blank(daily ? DAY : HOUR, (kpi && NOT_MEASURED[kpi]) || ENTITY_SALES_NOTE);
    if (!daily) return blank(HOUR, accountOnly(q.filters) ? ACCOUNT_HOURLY_NOTE : ENTITY_HOURLY_NOTE);
    const tax = ctx.metaTaxPct();
    const pick = (d: RealInsightDay | undefined): number | null => {
      if (q.key === 'spendByDay') return d?.spendCents ?? 0;
      if (q.key === 'revenueByDay' || q.key === 'revenueCumulative') return d?.metaValueCents ?? 0;
      if (!kpi) return null;
      const order = ENTITY_ORDER_PICK[kpi];
      if (order) return d ? order(d) : 0;
      if (kpi === 'spendAm') return d?.spendCents ?? 0;
      if (kpi === 'mediaCost') return d?.costCents ?? 0;
      return dayValue(kpi, d, null, 0);
    };
    const values = async (from: number, to: number, end: number, cut: number) => {
      const days = daysOf(await entityRows({ from, to }, q.filters), todayKey(from), tax);
      const axis = buckets([], from, end, DAY, cut, () => 0);
      const v = axis.t.map((b, i) => (axis.values[i] == null ? null : pick(days.get(todayKey(b)))));
      return { t: axis.t, values: q.key === 'revenueCumulative' ? cumulative(v) : v };
    };
    const main = await values(r.from, r.to, r.end, now);
    const out: LiveSeries = {
      t: main.t,
      series: [{ key, label, role: 'primary', values: main.values }],
      markers: [],
      freshness: fresh,
    };
    // Comparação só com dias inteiros (a Meta não dá a hora de uma campanha).
    const cEnd = cr ? cr.end : 0;
    if (cr && q.compare !== 'none' && wholeDay(cEnd))
      out.series.push({
        key: `${key}_cmp`,
        label: 'Comparação',
        role: 'compare',
        values: (await values(cr.from, cEnd, cEnd, Number.POSITIVE_INFINITY)).values,
      });
    return out;
  }

  /** Valor de um KPI de mídia num dia (`d` = a linha da Meta do dia; `tot` = as vendas reais do dia). */
  const dayValue = (
    kpi: KpiId,
    d: RealMedia['daily'][number] | undefined,
    tot: Totals | null,
    fixed: number,
  ): number | null => {
    const spend = d?.spendCents ?? 0;
    const cost = d ? (d.costCents ?? Math.round(spend / (1 - ctx.metaTaxPct()))) : 0;
    const per = (a: number | null | undefined, b: number | null | undefined, k = 1) =>
      a != null && b ? (a / b) * k : null;
    switch (kpi) {
      case 'cpa': {
        if (d?.metaPurchases == null) return null;
        const course = d.metaPurchases - (d.metaUpsell ?? 0);
        return course > 0 ? Math.round(spend / course) : null;
      }
      case 'roas':
        return per(d?.metaValueCents, spend);
      case 'roi':
        return per(d?.metaValueCents, cost);
      case 'impressions':
        return d?.impressions ?? (d ? null : 0);
      case 'linkClicks':
        return d?.linkClicks ?? (d ? null : 0);
      case 'lpv':
        return d?.lpv ?? (d ? null : 0);
      case 'cpm':
        return per(spend, d?.impressions, 1000);
      case 'ctrLink':
        return per(d?.linkClicks, d?.impressions);
      case 'cpcLink':
        return per(spend, d?.linkClicks);
      case 'connectRate':
        return per(d?.lpv, d?.linkClicks);
      case 'mer':
        return tot ? per(tot.gross, cost) : null;
      case 'roasReal':
        return tot ? per(finalOf(tot), cost) : null;
      case 'profit':
        return tot ? finalOf(tot) - cost : null;
      case 'margin':
        return tot?.gross ? (finalOf(tot) - cost) / tot.gross : null;
      case 'cpaBlended':
        return tot?.front ? spend / tot.front : null;
      case 'profitOperating':
        return tot ? finalOf(tot) - cost - fixed : null;
      default:
        return null;
    }
  };

  /** Vendas reais (Kiwify) de cada dia do intervalo, com os reembolsos pela data do reembolso. */
  async function salesByDay(q: SeriesQuery, from: number, end: number): Promise<Map<number, Totals>> {
    const [rows, refunds] = await Promise.all([
      ctx.load(from, end),
      ctx.loadRefunds ? ctx.loadRefunds(from, end) : Promise.resolve(null),
    ]);
    const group = (xs: RealOrderRow[], at: (o: RealOrderRow) => number) => {
      const g = new Map<number, RealOrderRow[]>();
      for (const o of xs) {
        if (!matches(o, q)) continue;
        const b = bucketOf(at(o), DAY);
        g.set(b, [...(g.get(b) ?? []), o]);
      }
      return g;
    };
    const byDay = group(rows, (o) => o.at);
    const refundsByDay = refunds ? group(refunds, (o) => orderDates(o).refundedAt ?? o.at) : null;
    const out = new Map<number, Totals>();
    for (let b = bucketOf(from, DAY); b < end; b += DAY)
      out.set(b, totals(byDay.get(b) ?? [], refundsByDay ? (refundsByDay.get(b) ?? []) : null));
    return out;
  }

  /**
   * CPA geral por hora, acumulado no dia (o detalhe do KPI desenha a razão acumulada): gasto real da
   * Meta até a hora ÷ vendas do curso pagas até a hora (Kiwify, todas as origens). Sem venda ainda,
   * sem ponto. Filtro que a Meta não recorta: sem série, como o número do topo.
   */
  async function blendedByHour(q: SeriesQuery): Promise<ColumnarSeries> {
    const now = clock();
    const r = rangeMs(q, now);
    const cr = compareMs(q, now);
    const cEnd = cr ? cr.end : 0;
    const [m, mc] = await Promise.all([
      ctx.loadMedia(r.from, r.to),
      cr ? ctx.loadMedia(cr.from, cEnd) : Promise.resolve(null),
    ]);
    if (!m) throw unavailable(r.from, r.to);
    if (noTodayYet(now, m, r)) throw metaUnavailable(META_GAP.noToday);
    const fresh = metaFreshness(now, m, r);
    if (offMetaDims(q.filters).length > 0) return { ...NO_SERIES, freshness: fresh };
    const cumulative = async (x: RealMedia, from: number, end: number, cut: number) => {
      const spend = buckets(
        x.hourly.map((p) => ({ ...row0, at: p.t, grossCents: p.spendCents })),
        from,
        end,
        HOUR,
        cut,
        (o) => o.grossCents,
      );
      const front = (await ctx.load(from, end)).filter(
        (o) => isPaid(o) && o.kind === 'front' && matches(o, q),
      );
      const sales = buckets(front, from, end, HOUR, cut, () => 1).values;
      let s = 0;
      let n = 0;
      return {
        t: spend.t,
        values: spend.values.map((v, i) => {
          if (v == null) return null;
          s += v;
          n += sales[i] ?? 0;
          return n > 0 ? s / n : null;
        }),
      };
    };
    const main = await cumulative(m, r.from, r.end, now);
    const out: ColumnarSeries = {
      t: main.t,
      series: [{ key: 'cpaBlended', label: 'No período', role: 'primary', values: main.values }],
      markers: [],
      freshness: fresh,
    };
    if (mc && cr)
      out.series.push({
        key: 'cpaBlended_cmp',
        label: 'Comparação',
        role: 'compare',
        values: (await cumulative(mc, cr.from, cEnd, Number.POSITIVE_INFINITY)).values,
      });
    return out;
  }

  /** Série dia a dia de um KPI de mídia (e a do período anterior), com as contas do número do topo. */
  async function dailyKpi(q: SeriesQuery, kpi: KpiId): Promise<ColumnarSeries> {
    const now = clock();
    const r = rangeMs(q, now);
    const cr = compareMs(q, now);
    const cEnd = cr ? cr.end : 0;
    const [m, mc] = await Promise.all([
      ctx.loadMedia(r.from, r.to),
      cr ? ctx.loadMedia(cr.from, cEnd) : Promise.resolve(null),
    ]);
    if (!m) throw unavailable(r.from, r.to);
    if (noTodayYet(now, m, r)) throw metaUnavailable(META_GAP.noToday);
    const fresh = metaFreshness(now, m, r);
    // Filtro que a Meta não recorta: o mesmo "sem valor" do número do topo.
    if (MIXED_KPIS.has(kpi) && offMetaDims(q.filters).length > 0) return { ...NO_SERIES, freshness: fresh };
    const needsSales = SALES_DAY_KPIS.has(kpi);
    const values = async (x: RealMedia, from: number, end: number, cut: number) => {
      const byDate = new Map(x.daily.map((d) => [Date.parse(`${d.date}T00:00:00Z`) + BRT, d]));
      const sales = needsSales ? await salesByDay(q, from, end) : null;
      const axis = buckets([], from, end, DAY, cut, () => 0);
      return {
        t: axis.t,
        values: axis.t.map((b, i) =>
          axis.values[i] == null
            ? null
            : dayValue(
                kpi,
                byDate.get(b),
                sales?.get(b) ?? null,
                kpi === 'profitOperating' ? fixedIn(b, Math.min(b + DAY, cut)) : 0,
              ),
        ),
      };
    };
    const main = await values(m, r.from, r.end, now);
    const out: ColumnarSeries = {
      t: main.t,
      series: [{ key: kpi, label: 'No período', role: 'primary', values: main.values }],
      markers: [],
      freshness: fresh,
    };
    if (mc && cr)
      out.series.push({
        key: `${kpi}_cmp`,
        label: 'Comparação',
        role: 'compare',
        values: (await values(mc, cr.from, cEnd, Number.POSITIVE_INFINITY)).values,
      });
    return out;
  }

  /**
   * Tabela da Meta real do período, ou o erro da fonte (Meta fora do ar ou não conectada): nunca a
   * tabela da simulação.
   */
  async function realTable(
    r: { from: number; to: number },
    level: 'campaign' | 'adset' | 'ad',
  ): Promise<RealInsights> {
    const [real, m] = await Promise.all([ctx.loadInsights(r.from, r.to, level), ctx.loadMedia(r.from, r.to)]);
    if (!m) throw unavailable(r.from, r.to);
    if (!real) throw metaUnavailable(gapText(mediaGap(ctx.loadInsights, r.from, r.to, level)));
    return real;
  }

  /**
   * Tabela de Tráfego real, recortada pelo filtro de conta, campanha, conjunto e anúncio (a tabela, o
   * total e o Resumo somam só o recorte); sem Meta, o erro da fonte (a tela mostra "Tentar de novo").
   */
  async function getInsights(q: InsightQuery): Promise<InsightTable> {
    const now = clock();
    const r = rangeMs(q, now);
    const real = await cutTable(r, q.level === 'account' ? 'campaign' : q.level, q.filters ?? {});
    return insightTable(real, q, {
      now,
      metaTaxPct: ctx.metaTaxPct(),
      freshness: metaFreshness(now, real, r),
    });
  }

  /**
   * Criativos: todos os anúncios reais da Meta no período, no recorte dos filtros (a tela agrupa pelo
   * nome do criativo e soma tudo). Sem página: cortar em 200 antes de agrupar deixava anúncios, gasto e
   * vendas de fora em 7D e 30D (o `limit` da consulta não vale aqui).
   */
  async function listCreatives(q: ListQuery): Promise<Page<unknown>> {
    const now = clock();
    const r = rangeMs(q, now);
    const real = await realTable(r, 'ad');
    const t = insightTable(
      real,
      { ...q, level: 'ad', sort: { key: 'spendAm', dir: 'desc' }, offset: 0, limit: real.rows.length },
      { now, metaTaxPct: ctx.metaTaxPct(), freshness: metaFreshness(now, real, r), all: true },
    );
    const s = q.search?.toLocaleLowerCase('pt-BR');
    const found = s ? t.rows.filter((x) => x.name.toLocaleLowerCase('pt-BR').includes(s)) : t.rows;
    const rows = groupFrequency(found);
    return { rows, nextCursor: null, total: rows.length };
  }

  /**
   * As três réguas do período e do recorte: a Meta completa (a do ROAS do topo), a Meta só pelo clique
   * (servidor novo, dias já relidos) e a Kiwify pelo link (pedidos aprovados com canal de anúncio). No
   * recorte por campanha, conjunto ou anúncio, as duas últimas ficam sem número (não se separam).
   */
  async function rulersOf(q: Query, r: { from: number; to: number }, m: RealMedia): Promise<AdRulers> {
    const cut = hasEntityFilter(q.filters);
    const orders = cut ? null : (await ctx.load(r.from, r.to)).filter((o) => matches(o, q));
    return buildAdRulers({
      spendCents: m.totals.spendCents,
      full: m.meta ? { ...m.meta, upsell: m.meta.upsell ?? 0 } : null,
      click: cut ? null : (m.metaClick ?? null),
      kiwify: orders ? kiwifyByLink(orders) : null,
      entityCut: cut,
    });
  }

  /**
   * ROAS do funil e custo por venda da Meta no período escolhido (Hoje, Ontem, 7D...). ROAS = valor das
   * compras da Meta ÷ valor gasto (sem imposto nem IOF), como o Gerenciador: decisão do dono. CPA =
   * investimento ÷ compras do curso (o 20X separado pelo valor: o teto já o soma por comprador).
   */
  const funnelV2 = (m: RealMedia) => {
    const spent = m.totals.spendCents;
    const n = adPurchases(m);
    const course = adCourseSales(m);
    return {
      roasFunnel: spent > 0 ? adValueCents(m) / spent : null,
      cpaMeta: course > 0 ? Math.round(m.totals.spendCents / course) : null,
      n,
      course,
    };
  };

  /**
   * Detalhe de um KPI de mídia: sem Meta, o erro da fonte (antes caía no detalhe da simulação). Com a
   * Meta, o número e o minigráfico do topo (os do `realKpis`) e a quebra por campanha real do período
   * (a mesma conta da tabela do Tráfego e das "Campanhas ao vivo"; vendas pela Meta). KPI que mistura
   * as vendas da Kiwify (MER, lucro...): sem quebra, porque a Kiwify não separa as vendas por campanha.
   */
  async function kpiDetail(q: Parameters<NonNullable<RealModule['kpiDetail']>>[0]) {
    if (!(MEDIA_KPI_IDS as readonly KpiId[]).includes(q.kpi)) return undefined;
    const now = clock();
    const r = rangeMs(q, now);
    const m = await ctx.loadMedia(r.from, r.to);
    if (!m) throw unavailable(r.from, r.to);
    if (noTodayYet(now, m, r)) throw metaUnavailable(META_GAP.noToday);
    const field = BY_CAMPAIGN[q.kpi];
    if (!field) return undefined;
    const [k, real] = await Promise.all([ctx.realKpis(q), cutTable(r, 'campaign', q.filters)]);
    const value = k.kpis[q.kpi] ?? noRealKpi(q.kpi, META_GAP.error);
    const table = insightTable(
      real,
      { ...q, level: 'campaign', sort: { key: 'spendAm', dir: 'desc' }, offset: 0, limit: real.rows.length },
      { now, metaTaxPct: ctx.metaTaxPct(), freshness: metaFreshness(now, real, r), all: true },
    );
    const byCampaign =
      value.value == null
        ? []
        : table.rows
            .filter((x) => x.spendAm > 0 || x.impressions > 0)
            .map((x) => {
              const v = x[field];
              return {
                id: x.id,
                name: x.name,
                value: typeof v === 'number' && Number.isFinite(v) ? v : null,
              };
            });
    const axis = buckets([], r.from, r.end, sparkSize(r), now, () => 0).t;
    const values = value.spark && value.spark.length === axis.length ? value.spark : axis.map(() => null);
    return {
      value,
      series: {
        t: axis,
        series: [{ key: q.kpi, label: q.kpi, role: 'primary' as const, values }],
        markers: [],
        freshness: value.freshness?.length ? value.freshness : metaFreshness(now, m, r),
      },
      byCampaign,
    };
  }

  /**
   * Contas, campanhas, conjuntos e anúncios reais (os da Meta nos últimos 30 dias e os ativos), para o
   * painel de Filtros, as abas de nível e os sheets. Sem a Meta: listas vazias, nunca as simuladas.
   */
  async function realEntities(): Promise<{
    accounts: AdAccount[];
    campaigns: Campaign[];
    adsets: AdSet[];
    ads: Ad[];
  }> {
    const now = clock();
    const from = dayStart(todayKey(now)) - 29 * DAY;
    const to = Math.ceil(now / 60_000) * 60_000;
    const [c, s, a] = await Promise.all(
      (['campaign', 'adset', 'ad'] as const).map((l) => ctx.loadInsights(from, to, l)),
    );
    // Sem a Meta (ou resposta fora do formato): listas vazias, nunca as simuladas.
    if (!Array.isArray(c?.rows) || !Array.isArray(s?.rows) || !Array.isArray(a?.rows))
      return { accounts: [], campaigns: [], adsets: [], ads: [] };
    const conf = (e: MetaEffectiveStatus): Campaign['status'] =>
      e === 'ACTIVE' || e === 'DELETED' || e === 'ARCHIVED' ? e : 'PAUSED';
    const slot = (i: number) => ((i % 6) + 1) as Campaign['colorSlot'];
    const accIds = [...new Set([...c.rows, ...s.rows, ...a.rows].map((x) => x.accountId))].sort((x, y) =>
      accountName(x).localeCompare(accountName(y), 'pt-BR'),
    );
    const tzOf = new Map([...c.rows, ...s.rows, ...a.rows].map((x) => [x.accountId, x.timezone ?? null]));
    const accounts: AdAccount[] = accIds.map((id, i) => ({
      id,
      name: accountName(id),
      currency: 'BRL',
      timezone: tzOf.get(id) ?? 'America/Sao_Paulo',
      status: 'ACTIVE',
      spendCap: null,
      colorSlot: slot(i),
    }));
    const campaigns: Campaign[] = c.rows
      .filter((x) => x.id !== 'sem_campanha')
      .map((x, i) => {
        const eff = effectiveStatus(x.status);
        return {
          id: x.id,
          accountId: x.accountId,
          name: x.name,
          objective: '',
          status: conf(eff),
          effectiveStatus: eff,
          isCbo: x.dailyBudget != null,
          dailyBudget: x.dailyBudget,
          lifetimeBudget: null,
          bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
          colorSlot: slot(i),
          issues: [],
          createdAt: x.createdAt ?? 0,
          edits: [],
        };
      });
    const adsets: AdSet[] = s.rows
      .filter((x) => x.id !== 'sem_conjunto')
      .map((x) => {
        const eff = effectiveStatus(x.status);
        return {
          id: x.id,
          campaignId: x.campaignId ?? '',
          accountId: x.accountId,
          name: x.name,
          status: conf(eff),
          effectiveStatus: eff,
          dailyBudget: x.dailyBudget,
          optimizationGoal: '',
          learning: { status: 'UNKNOWN', events7d: 0, lastSignificantEditAt: null },
          audienceType: 'cold',
          budgetChangesLastHour: 0,
          lastEditAt: null,
          issues: [],
          createdAt: x.createdAt ?? 0,
          edits: [],
        };
      });
    const ads: Ad[] = a.rows.map((x) => {
      const eff = effectiveStatus(x.status);
      return {
        id: x.id,
        adsetId: x.adsetId ?? '',
        campaignId: x.campaignId ?? '',
        accountId: x.accountId,
        name: x.name,
        status: conf(eff),
        effectiveStatus: eff,
        // Sem código de criativo na Meta: a tela usa o nome do anúncio ("AD 05", "AERO 07").
        creative: {
          id: x.id,
          code: '',
          format: (x.videoViews ?? 0) > 0 ? 'video' : 'image',
          thumbnailUrl: x.thumbnail ?? '',
          firstImpressionAt: x.createdAt ?? 0,
        },
        issues: [],
        edits: [],
      };
    });
    return { accounts, campaigns, adsets, ads };
  }

  return {
    kpis,
    kpiIds: MEDIA_KPI_IDS,
    // A mídia do stream simulado nunca passa: sem número real, o cartão fica sem valor.
    neverSimKpis: MEDIA_KPI_IDS,
    series,
    kpiDetail,
    overrides: { getInsights },
    list: (kind, q) => (kind === 'creatives' ? listCreatives(q) : undefined),
    onMediaSync: () => ctx.invalidateMedia(),
    // Ajuda e fórmula dos KPIs de mídia com a conta que o modo ao vivo faz (a simulação segue a dela),
    // e as contas, campanhas, conjuntos e anúncios reais da Meta no lugar dos simulados.
    patchBoot: async (boot) => ({ ...withLiveKpiTexts(boot), ...(await realEntities()) }),
    async patchSnapshot(q, snap, real) {
      const m0 = real.media;
      const now = clock();
      const r = rangeMs(q, now);
      // Selo da Meta no rodapé: hora dos dados mostrados (hoje: a última leitura de hoje; Ontem: a
      // linha mais nova do período, não a última sincronização) e amarelo quando atrasado ou com erro.
      if (m0 && snap.freshness)
        snap.freshness = snap.freshness.flatMap((f) =>
          f.source === 'meta' ? metaFreshness(now, m0, r) : [f],
        );
      let missing = mediaMissing(ctx.loadMedia, m0, now, r);
      // Recorte por campanha, conjunto ou anúncio: as compras, o ROAS e o CPA da Meta são do recorte.
      let m = m0;
      if (!missing && m0 && hasEntityFilter(q.filters))
        try {
          m = asMedia(m0, await entityRows(r, q.filters));
        } catch (e) {
          missing = errorText(e);
        }
      // Compras que a Meta atribui aos anúncios (o número do Gerenciador), ao lado do canal pela UTM.
      // Hoje antes da primeira leitura: nada ("0 vendas na Meta" seria falso).
      if (!missing && m?.meta) snap.adsMeta = { purchases: m.meta.purchases, valueCents: m.meta.valueCents };
      else delete snap.adsMeta;
      // As três réguas das vendas de anúncio (Meta completa, só clique e Kiwify pelo link), as mesmas
      // no Hoje e no Tráfego: os dois leem este snapshot.
      withAdRulers(snap, !missing && m ? await rulersOf(q, r, m) : null);
      const k2 = snap.kpisV2;
      if (!k2) return;
      // Sem Meta (ou hoje ainda sem leitura): ROAS do funil e CPA da Meta sem valor, nunca os da simulação.
      if (missing || !m) {
        const motivo = missing ?? META_GAP.error;
        if (k2.roasFunnel) k2.roasFunnel = noRealKpi('roasFunnel', motivo);
        if (k2.cpaMeta) k2.cpaMeta = noRealKpi('cpaMeta', motivo);
        return;
      }
      // ROAS do funil e custo por venda da Meta: com as compras que a Meta atribui aos anúncios (regra
      // do dono: venda de anúncio conta pela Meta).
      const fresh = metaFreshness(now, m, r) as NonNullable<typeof k2.roasFunnel>['freshness'];
      const base = (id: 'roasFunnel' | 'cpaMeta') => ({
        ...(k2[id] ?? { deltaKind: 'relative', status: 'neutral' }),
        id,
        compareValue: null,
        delta: null,
        freshness: fresh,
      });
      const f = funnelV2(m);
      const off = offMetaDims(q.filters).length > 0;
      k2.roasFunnel = {
        ...base('roasFunnel'),
        value: f.roasFunnel,
        sample: { n: f.n, min: 10, unit: 'vendas' },
        note: off ? ACCOUNT_NOTE : ROAS_NOTE,
      } as NonNullable<typeof k2.roasFunnel>;
      // Filtro que a Meta não recorta: o CPA sai, como o `cpa` (o teto ao lado é do recorte).
      k2.cpaMeta = off
        ? noRealKpi('cpaMeta', OFF_META_NOTE)
        : ({
            ...base('cpaMeta'),
            value: f.cpaMeta,
            sample: { n: f.course, min: 10, unit: 'vendas' },
            note: CPA_NOTE,
          } as NonNullable<typeof k2.cpaMeta>);
    },
    async patchKpisV2(q, out, sim) {
      const now = clock();
      const r = rangeMs(q, now);
      const [m0, ins] = await Promise.all([
        ctx.loadMedia(r.from, r.to),
        ctx.loadInsights(r.from, r.to, 'campaign'),
      ]);
      const kiwi = ctx.kiwiFresh(now);
      let missing = mediaMissing(ctx.loadMedia, m0, now, r);
      // Recorte por campanha, conjunto ou anúncio: custo por visita, ROAS e CPA da Meta do recorte.
      let m = m0;
      if (!missing && m0 && hasEntityFilter(q.filters))
        try {
          m = asMedia(m0, await entityRows(r, q.filters));
        } catch (e) {
          missing = errorText(e);
        }
      if (missing || !m) {
        // Sem Meta: os KPIs de mídia da V2 ficam sem valor com o motivo (nunca os da simulação).
        const motivo = missing ?? META_GAP.error;
        for (const id of ['costPerVisit', 'roasFunnel', 'cpaMeta'] as const)
          if (q.ids.includes(id)) out[id] = noRealKpi(id, motivo);
        if (ins)
          setKpiV2(q, out, sim, 'utmContentCoverage', ins.utmContentCoverage, kiwi, {
            note: 'vendas de anúncio com utm_content',
          });
        else if (q.ids.includes('utmContentCoverage'))
          out.utmContentCoverage = noRealKpi('utmContentCoverage', motivo);
        return;
      }
      const meta = metaFreshness(now, m, r);
      setKpiV2(
        q,
        out,
        sim,
        'costPerVisit',
        m.totals.lpv > 0 ? Math.round(m.totals.spendCents / m.totals.lpv) : null,
        meta,
        {
          note: 'investimento ÷ visitas à página (Meta)',
        },
      );
      setKpiV2(q, out, sim, 'utmContentCoverage', ins?.utmContentCoverage ?? null, kiwi, {
        note: ins?.untrackedSales
          ? `${ins.untrackedSales} vendas de anúncio sem ligar a um anúncio`
          : 'vendas de anúncio com utm_content',
      });
      // ROAS e CPA da Meta no período escolhido (antes: janela fixa de 7 dias).
      const f = funnelV2(m);
      const off = offMetaDims(q.filters).length > 0;
      setKpiV2(q, out, sim, 'roasFunnel', f.roasFunnel, meta, {
        sample: { n: f.n, min: 10, unit: 'vendas' },
        note: off ? ACCOUNT_NOTE : ROAS_NOTE,
      });
      if (off && q.ids.includes('cpaMeta')) out.cpaMeta = noRealKpi('cpaMeta', OFF_META_NOTE);
      else
        setKpiV2(q, out, sim, 'cpaMeta', f.cpaMeta, meta, {
          sample: { n: f.course, min: 10, unit: 'vendas' },
          note: CPA_NOTE,
        });
    },
  };
}
